import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    FEATURE_GENERIC_TASKS,
    PersonsOrganisationDto,
    PreferencesService,
    UserService,
    WorkTracker,
    hasAnyPermission,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { EMPTY, Subject, combineLatest } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../../outstanding-tasks-view.service';
import {
    AuthorityDocumentGroup,
    AuthorityDocumentsDataService
} from './authority-documents-data.service';

const AUTHORITY_DOCUMENTS_ACKNOWLEDGEMENT =
    'authorityDocumentsAcknowledgementWidget';

/**
 * Displays authority documents that require the current user's acknowledgement,
 * grouped by their source authority. Uses the attachment-list component for each
 * group, which natively handles viewing, downloading, and acknowledging.
 * A group is hidden once all its documents have been acknowledged.
 */
@Component({
    selector: 'authority-documents-acknowledgement-widget',
    templateUrl: './authority-documents-widget.component.html',
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()],
    providers: [AuthorityDocumentsDataService]
})
export class AuthorityDocumentsAcknowledgementWidget
    implements OnInit, OnDestroy
{
    organisation: PersonsOrganisationDto;
    showBody = true;

    groups: AuthorityDocumentGroup[] = [];
    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    canViewOthers = false;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private authorityDocumentsDataService: AuthorityDocumentsDataService,
        private workspaceStateService: WorkspaceStateService,
        private userService: UserService,
        private preferencesService: PreferencesService
    ) {
        combineLatest([
            this.authorityDocumentsDataService.working$,
            this.workTracker.observable
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([serviceWorking, working]) =>
                    (this.working = working || serviceWorking)
            );
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.loadPreferences();
                this.refreshPermissions();
            });

        this.authorityDocumentsDataService.groups$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(groups => (this.groups = groups));
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    private refreshPermissions() {
        this.canViewOthers = hasAnyPermission(
            this.organisation,
            PersonsOrganisationDto.Permissions.PERSON_LIST
        );
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }
        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                'authorityDocumentsWidgetTable',
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.applyPreferences({});
                    return EMPTY;
                })
            )
            .subscribe(preferences => {
                this.applyPreferences(preferences ?? {});
            })
            .add(this.workTracker.createTracker());
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = { ...preferences };
        this.showBody = preferences.showBody ?? true;
        this.loadGroups();
    }

    private loadGroups() {
        if (!this.organisation) {
            return;
        }
        const personId = this.userService.getCurrentUser()?.id;
        this.authorityDocumentsDataService.loadGroups(
            this.organisation.id,
            personId
        );
    }

    updateUserPreferences() {
        if (!this.organisation) {
            return;
        }
        this.preferencesService
            .updatePreferences(
                'authorityDocumentsWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }
}

export const authorityDocumentsAcknowledgementWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: AUTHORITY_DOCUMENTS_ACKNOWLEDGEMENT,
        section: 'profile',
        component: AuthorityDocumentsAcknowledgementWidget,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_GENERIC_TASKS),
        requiredPermissions: [
            PersonsOrganisationDto.Permissions.ORGANISATION_VIEW
        ]
    };
